import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { CurrentBanner } from 'types/banner';
import { CurrentModal } from 'types/modal';
import InvestorUpdateBanner from '@components/layout/investor-update-banner.vue';
import { Route } from 'vue-router';
import { RouteTransition } from 'types/layout';
import { ScreenSizeType } from 'types/client';
import store from '..';
import { Toast } from 'types/layout';
import { verifyPassword } from '@api/user';

export interface AppState {
	currentModal: CurrentModal | null;
	isAuthenticated: boolean;
	screenSize: ScreenSizeType;
	oauthToken: string;
	toasts: Array<Toast>;
}

@Module({
	dynamic: true,
	namespaced: true,
	name: 'app',
	store
})
class AppModule extends VuexModule {
	currentModal: CurrentModal | null = null;
	currentBanner: CurrentBanner | null = null;

	isAuthenticated = false;

	oauthToken = `${window.fundriseApp.oAuthToken}`;

	routeTransition: RouteTransition = {
		scope: 'fullpage',
		name: 'fade'
	};

	screenSize: ScreenSizeType = 'DESKTOP';

	subPageHeight: number | null = null;

	toasts: Array<Toast> = window.fundriseApp.initialToasts;

	@Action({ rawError: true })
	public async getCurrentBanner(routeKey?: string): Promise<void> {
		const customBannerPageList = [
			'/acq-plus/start',
			'/partner'
		];
		if (routeKey && customBannerPageList.includes(routeKey)) {
			this.UPDATE_CURRENT_BANNER(null);
		} else {
			this.UPDATE_CURRENT_BANNER({
				banner: InvestorUpdateBanner
			});
		}
	}

	@Action({ rawError: true })
	public async getPrivilegedToken(password: string): Promise<void> {
		const accessToken = await verifyPassword(password);
		this.SET_OAUTH_TOKEN(accessToken);
	}

	@Mutation
	public SET_IS_AUTHENTICATED(isAuthenticated: boolean): void {
		this.isAuthenticated = isAuthenticated;
	}

	@Mutation
	public UPDATE_CURRENT_MODAL(currentModal: CurrentModal | null): void {
		this.currentModal = currentModal;
	}

	@Mutation
	public UPDATE_CURRENT_BANNER(currentBanner: CurrentBanner | null): void {
		this.currentBanner = currentBanner;
	}

	@Mutation
	public SET_OAUTH_TOKEN(token: string): void {
		this.oauthToken = token;
		window.fundriseApp.oAuthToken = token;
	}

	@Mutation
	public ADD_TOAST(toastToAdd: Toast): void {
		const newToast = this.toasts.filter((toast) => toast.message === toastToAdd.message).length <= 0;

		if (newToast) {
			this.toasts.push(toastToAdd);
		}
	}

	@Mutation
	public DISMISS_TOAST(dismissed: Toast): void {
		this.toasts = this.toasts.filter((toast) => toast.message !== dismissed.message);
	}

	@Mutation
	public UPDATE_ROUTE_TRANSITION(payload: { to: Route; from: Route }): void {
		const toStepIndex =
			typeof payload.to.meta === 'function'
				? payload.to.meta(payload.to).step
				: typeof payload.to.meta.step === 'number'
				? payload.to.meta.step
				: undefined;
		const fromStepIndex =
			typeof payload.from.meta === 'function'
				? payload.from.meta(payload.from).step
				: typeof payload.from.meta.step === 'number'
				? payload.from.meta.step
				: undefined;

		if (toStepIndex && fromStepIndex) {
			this.routeTransition.scope = 'subpage';
			if (fromStepIndex < toStepIndex) {
				this.routeTransition.name = 'slide-left';
			} else if (fromStepIndex > toStepIndex) {
				this.routeTransition.name = 'slide-right';
			} else {
				this.routeTransition.name = 'fade';
			}
		} else {
			this.routeTransition.scope = 'fullpage';
			this.routeTransition.name = 'fade';
		}
	}

	@Mutation
	public UPDATE_SCREEN_SIZE(screenSize: ScreenSizeType): void {
		if (screenSize !== this.screenSize) {
			this.screenSize = screenSize;
		}
	}

	@Mutation
	public UPDATE_SUB_PAGE_HEIGHT(height: number): void {
		if (height !== this.subPageHeight) {
			this.subPageHeight = height;
		}
	}
}

export const app = getModule(AppModule);
